<template>
    <div class="space-y-2">
        <InlineCreateAllocationVariant
            v-if="canCreateVariant"
            :status="inlineCreateStatus"
            @click="createNewInlineVariant"
        />
        <div v-if="!loading && variantsRef.length > 0" class="space-y-2">
            <ProjectVariantCard
                v-for="variant in variantsRef"
                ref="variantCardCompositionRef"
                :key="variant.id"
                :project-id="projectId"
                :variant="variant"
                :variant-type="variantType"
                :variants-ref="variantsRef"
                :phase-types="projectPhaseTypes"
                :link-to-reference-allocation="{
                    name: 'project-overview-tab',
                    params: { projectId },
                }"
                :reference-allocation-query-document="getReferenceAllocationQueryDocument(variant)"
                :reference-allocation-link-label="$t('phases-tile-title-reference-allocation')"
                :set-variants-ref="setVariantsRef"
                :set-current-variant-loading="setCurrentVariantLoading"
                :set-current-variant-error="setCurrentVariantError"
                @set-current-variant="(variantId) => onSetCurrentVariant(variantId)"
            />
        </div>
    </div>
</template>
<script setup lang="ts">
import auth from '@/auth/auth';
import InlineCreateAllocationVariant from '@/components/InlineCreateAllocationVariant/InlineCreateAllocationVariant.vue';
import { mapToPhaseColor } from '@/components/Timeline/PhaseColorMappings';
import {
    GetProjectAllocationUsagesDocument,
    ProjectAllocationAreaPhaseTileDataDocument,
    ProjectAllocationRoomPhaseTileDataDocument,
} from '@/generated/graphql';
import ProjectVariantCard, { ProjectVariantCardExpose } from '@/project/components/ProjectVariantCard.vue';
import { useProjectInjection } from '@/project/composables/useProjectInjection';
import { useProjectVariants } from '@/project/composables/useProjectVariants';
import { VariantCardVariant } from '@/variant/types';
import { useQuery } from '@vue/apollo-composable';
import { useTemplateRefsList } from '@vueuse/core';
import { computed, Ref } from 'vue';

const props = defineProps<{ projectId: string }>();
const projectId = computed(() => props.projectId);
const variantType = 'Project';

const variantCardCompositionRef: Ref<ProjectVariantCardExpose[]> = useTemplateRefsList();

const { project } = useProjectInjection();
const canCreateVariant = computed(() => auth.isAdmin());

const {
    variantsRef,
    loading,
    setCurrentVariantLoading,
    setCurrentVariantError,
    inlineCreateStatus,
    setVariantsRef,
    setCurrentVariant,
    createNewInlineVariant,
} = useProjectVariants(project, variantCardCompositionRef);

async function onSetCurrentVariant(variantId: string) {
    setCurrentVariant(variantId);
}

function getReferenceAllocationQueryDocument(variant: VariantCardVariant) {
    if (variant.referenceAllocationAreaId) {
        return ProjectAllocationAreaPhaseTileDataDocument;
    }
    return ProjectAllocationRoomPhaseTileDataDocument;
}

// as the usages do not change during a session we can use the cache here
const { result: allocationUsages } = useQuery(GetProjectAllocationUsagesDocument, null, {
    fetchPolicy: 'cache-first',
});

const projectPhaseTypes = computed(
    () =>
        allocationUsages.value?.projectAllocationUsages?.nodes.map((n) => ({
            ...n,
            color: mapToPhaseColor(n.type),
        })) ?? [],
);
</script>
